import { Tables } from "@/integrations/supabase/types";
import { fetchApi } from "./base";

export type AlertCardItem = {
  card: Tables<"cards"> & { set: Tables<"sets">; game: Tables<"games"> };
  alert: Tables<"price_alers">;
};

export type DealHunterFilters = {
  cardId: number;
  language: "english" | "japanese" | "chinese";
  cardFormat: "any" | "raw" | "graded";
  condition:
    | "any"
    | "near_mint"
    | "lightly_played"
    | "moderately_played"
    | "heavily_played"
    | "damaged";
  printing:
    "any" | "normal" | "holofoil" | "reverse_holofoil" | "first_edition";
  gradingCompany: "any" | "psa" | "cgc" | "bgs" | "sgc";
  grade:
    | "any"
    | "1"
    | "1.5"
    | "2"
    | "2.5"
    | "3"
    | "3.5"
    | "4"
    | "4.5"
    | "5"
    | "5.5"
    | "6"
    | "6.5"
    | "7"
    | "7.5"
    | "8"
    | "8.5"
    | "9"
    | "9.5"
    | "10";
  minimumDiscountPercent: number;
  maximumTotalPrice?: number;
  minimumSellerFeedbackPercent: number;
};

export type DealHunterEntitlement = {
  tier: "free" | "collector_pro";
  hasFullAccess: boolean;
  maxResults: number;
  automatedAlerts: boolean;
  maxSavedSearches: number;
};

export type SavedDealHunterSearch = {
  id: number;
  cardId: number;
  language: DealHunterFilters["language"];
  cardFormat: DealHunterFilters["cardFormat"];
  condition: DealHunterFilters["condition"];
  printing: DealHunterFilters["printing"];
  gradingCompany: DealHunterFilters["gradingCompany"];
  grade: DealHunterFilters["grade"];
  minimumDiscountPercent: number;
  maximumTotalPrice: number | null;
  minimumSellerFeedbackPercent: number;
  enabled: boolean;
  lastCheckedAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  card: {
    id: number;
    name: string;
    number: string | null;
    setName: string | null;
  };
};

export type DealHunterMatch = {
  id: number;
  searchId: number;
  source: "ebay";
  sourceItemId: string;
  title: string;
  itemUrl: string;
  imageUrl: string | null;
  itemPrice: number;
  shippingPrice: number;
  totalPrice: number;
  currency: string;
  referenceMarketPrice: number;
  discountPercent: number;
  dealScore: number;
  matchConfidence: number;
  recommendation: string;
  riskFlags: string[];
  sellerName: string | null;
  sellerFeedbackPercent: number | null;
  detectedAt: string;
  notifiedAt: string | null;
  card: {
    id: number;
    name: string;
    number: string | null;
    setName: string | null;
  };
};

export type LiveDealListing = {
  source: "ebay";
  itemId: string;
  title: string;
  imageUrl: string | null;
  itemUrl: string;
  itemPrice: number;
  shippingPrice: number;
  totalPrice: number;
  currency: string;
  discountPercent: number;
  dealScore: number;
  matchConfidence: number;
  matchLabel: "strong" | "likely" | "review";
  recommendation: string;
  riskFlags: string[];
  condition: string | null;
  sellerName: string | null;
  sellerFeedbackPercent: number | null;
  topRatedBuyingExperience: boolean;
  buyingOptions: string[];
  listedAt: string | null;
};

export type DealHunterListingsResponse = {
  success: true;
  data: {
    card: {
      id: number;
      name: string;
      number: string | null;
      setName: string | null;
      tcgplayerId: string | null;
      language: "english" | "japanese" | "chinese";
    };
    identity: {
      cardId: number;
      cardFormat: "raw" | "graded";
      language: "english" | "japanese" | "chinese";
      condition: string | null;
      printing: string | null;
      gradingCompany: string | null;
      grade: string | null;
      exactCardNumberRequired: boolean;
    };
    truePriceConfidence: {
      score: number;
      band: "high" | "medium" | "low";
      includedObservations: number;
      excludedObservations: number;
      sourceCount: number;
    };
    referenceMarketPrice: number;
    referenceSource:
      | "PokeLlama True Price"
      | "PokeLlama raw market"
      | "PokeLlama graded market";
    minimumDiscountPercent: number;
    listings: LiveDealListing[];
    ebayResultCount: number;
    checkedAt: string;
    entitlement: DealHunterEntitlement;
  };
};

export function getAlerts() {
  return fetchApi<{
    success: boolean;
    data: AlertCardItem[];
  }>(`/api/price-alerts`, {
    method: "GET",
  });
}

export function getAlertsByCardId(cardId: number) {
  return fetchApi<any[]>(`/api/price-alerts/${cardId}`, {
    method: "GET",
  });
}

export function searchDealHunterListings(filters: DealHunterFilters) {
  const params = new URLSearchParams({
    cardId: String(filters.cardId),
    language: filters.language,
    cardFormat: filters.cardFormat,
    condition: filters.condition,
    printing: filters.printing,
    gradingCompany: filters.gradingCompany,
    grade: filters.grade,
    minimumDiscountPercent: String(filters.minimumDiscountPercent),
    minimumSellerFeedbackPercent: String(filters.minimumSellerFeedbackPercent),
  });

  if (filters.maximumTotalPrice) {
    params.set("maximumTotalPrice", String(filters.maximumTotalPrice));
  }

  return fetchApi<DealHunterListingsResponse>(
    `/api/price-alerts/deal-hunter/listings?${params.toString()}`,
    { method: "GET" },
  );
}

export function getSavedDealHunterSearches() {
  return fetchApi<{
    success: true;
    data: SavedDealHunterSearch[];
    entitlement: DealHunterEntitlement;
  }>("/api/price-alerts/deal-hunter/saved-searches", { method: "GET" });
}

export function createSavedDealHunterSearch(filters: DealHunterFilters) {
  return fetchApi<{
    success: true;
    data: SavedDealHunterSearch;
    initialCheck: {
      checkedAt: string;
      newMatchCount: number;
      notifiedCount: number;
      qualifyingListingCount: number;
    } | null;
    initialCheckError: string | null;
  }>("/api/price-alerts/deal-hunter/saved-searches", {
    method: "POST",
    body: filters,
  });
}

export function setSavedDealHunterSearchEnabled(
  searchId: number,
  enabled: boolean,
) {
  return fetchApi<{ success: true; data: SavedDealHunterSearch }>(
    `/api/price-alerts/deal-hunter/saved-searches/${searchId}`,
    { method: "PATCH", body: { enabled } },
  );
}

export function checkSavedDealHunterSearch(searchId: number) {
  return fetchApi<{
    success: true;
    data: {
      checkedAt: string;
      newMatchCount: number;
      notifiedCount: number;
      qualifyingListingCount: number;
    };
  }>(`/api/price-alerts/deal-hunter/saved-searches/${searchId}/check`, {
    method: "POST",
  });
}

export function deleteSavedDealHunterSearch(searchId: number) {
  return fetchApi<void>(
    `/api/price-alerts/deal-hunter/saved-searches/${searchId}`,
    { method: "DELETE" },
  );
}

export function getDealHunterMatches(limit = 50) {
  return fetchApi<{ success: true; data: DealHunterMatch[] }>(
    `/api/price-alerts/deal-hunter/matches?limit=${limit}`,
    { method: "GET" },
  );
}
